import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Check,
  Calculator,
  ChevronsUpDown,
  Database,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  Truck,
} from "lucide-react";
import AdminLayout from "../components/AdminLayout";
import AdminPagination from "../components/AdminPagination";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../components/ui/command";
import { shippingApi } from "../lib/api";
import { formatRupiah } from "../lib/format";
import { cn } from "../lib/utils";

const emptyCourier = {
  id: "",
  code: "",
  name: "",
  description: "",
  is_active: true,
  sort_order: 0,
};

const PAGE_SIZE = 10;
const MOJOROTO_PRESET = {
  province: "JAWA TIMUR",
  city: "KEDIRI",
};

const AdminCouriersPage = () => {
  const [couriers, setCouriers] = useState([]);
  const [courierPage, setCourierPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyCourier);

  const [locations, setLocations] = useState({
    provinces: [],
    cities: [],
    districts: [],
    subdistricts: [],
  });
  const [selected, setSelected] = useState({
    provinceId: "",
    cityId: "",
    districtId: "",
    subdistrictId: "",
  });
  const [locationLoading, setLocationLoading] = useState("");
  const [costForm, setCostForm] = useState({
    origin: "",
    destination: "",
    weight: "1000",
    price: "lowest",
    calculation_type: "domestic",
  });
  const [costResult, setCostResult] = useState(null);
  const [costSearch, setCostSearch] = useState("");
  const [calculating, setCalculating] = useState(false);
  const [originPicker, setOriginPicker] = useState({
    provinceId: "",
    cityId: "",
    districtId: "",
    provinces: [],
    cities: [],
    districts: [],
    subdistricts: [],
  });
  const [destinationPicker, setDestinationPicker] = useState({
    provinceId: "",
    cityId: "",
    districtId: "",
    provinces: [],
    cities: [],
    districts: [],
    subdistricts: [],
  });

  const activeCouriers = useMemo(
    () => couriers.filter((courier) => courier.is_active),
    [couriers],
  );
  const totalCourierPages = Math.max(1, Math.ceil(couriers.length / PAGE_SIZE));
  const paginatedCouriers = couriers.slice(
    (courierPage - 1) * PAGE_SIZE,
    courierPage * PAGE_SIZE,
  );

  useEffect(() => {
    if (courierPage > totalCourierPages) {
      setCourierPage(totalCourierPages);
    }
  }, [courierPage, totalCourierPages]);

  const loadCouriers = async () => {
    try {
      setLoading(true);
      const data = await shippingApi.getCouriers();
      setCouriers(data || []);
    } catch (error) {
      toast.error(error.message || "Gagal memuat kurir");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCouriers();
    Promise.all([loadPickerProvinces("origin"), loadPickerProvinces("destination")]).catch((error) => {
      toast.error(error.message || "Gagal memuat provinsi awal");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => {
    setForm(emptyCourier);
    setDialogOpen(true);
  };

  const openEdit = (courier) => {
    setForm({
      ...courier,
      sort_order: courier.sort_order || 0,
      description: courier.description || "",
    });
    setDialogOpen(true);
  };

  const setField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const saveCourier = async (event) => {
    event.preventDefault();
    if (!form.code.trim()) return toast.error("Kode kurir wajib diisi");
    if (!form.name.trim()) return toast.error("Nama kurir wajib diisi");

    const payload = {
      code: form.code,
      name: form.name,
      description: form.description || null,
      is_active: form.is_active,
      sort_order: Number(form.sort_order || 0),
    };

    try {
      setSaving(true);
      if (form.id) {
        await shippingApi.updateCourier(form.id, payload);
        toast.success("Kurir diperbarui");
      } else {
        await shippingApi.createCourier(payload);
        toast.success("Kurir ditambahkan");
      }
      setDialogOpen(false);
      await loadCouriers();
    } catch (error) {
      toast.error(error.message || "Gagal menyimpan kurir");
    } finally {
      setSaving(false);
    }
  };

  const seedDefault = async () => {
    try {
      setSaving(true);
      const data = await shippingApi.seedDefaultCouriers();
      setCouriers(data || []);
      toast.success("Daftar kurir default disiapkan");
    } catch (error) {
      toast.error(error.message || "Gagal seed kurir");
    } finally {
      setSaving(false);
    }
  };

  const removeCourier = async (courier) => {
    try {
      await shippingApi.deleteCourier(courier.id);
      toast.success("Kurir dihapus");
      await loadCouriers();
    } catch (error) {
      toast.error(error.message || "Gagal menghapus kurir");
    }
  };

  const loadProvinces = async (refresh = false) => {
    try {
      setLocationLoading("province");
      const data = await shippingApi.getProvinces({ refresh });
      setLocations((current) => ({ ...current, provinces: data || [] }));
      toast.success(refresh ? "Provinsi direfresh dari RajaOngkir" : "Provinsi dimuat");
    } catch (error) {
      toast.error(error.message || "Gagal memuat provinsi");
    } finally {
      setLocationLoading("");
    }
  };

  const loadCities = async (provinceId, refresh = false) => {
    if (!provinceId) return;
    try {
      setLocationLoading("city");
      const data = await shippingApi.getCities({ province_id: provinceId, refresh });
      setLocations((current) => ({
        ...current,
        cities: data || [],
        districts: [],
        subdistricts: [],
      }));
      setSelected((current) => ({ ...current, cityId: "", districtId: "", subdistrictId: "" }));
    } catch (error) {
      toast.error(error.message || "Gagal memuat kota/kabupaten");
    } finally {
      setLocationLoading("");
    }
  };

  const loadDistricts = async (cityId, refresh = false) => {
    if (!cityId) return;
    try {
      setLocationLoading("district");
      const data = await shippingApi.getDistricts({ city_id: cityId, refresh });
      setLocations((current) => ({
        ...current,
        districts: data || [],
        subdistricts: [],
      }));
      setSelected((current) => ({ ...current, districtId: "", subdistrictId: "" }));
    } catch (error) {
      toast.error(error.message || "Gagal memuat kecamatan");
    } finally {
      setLocationLoading("");
    }
  };

  const loadSubdistricts = async (districtId, refresh = false) => {
    if (!districtId) return;
    try {
      setLocationLoading("subdistrict");
      const data = await shippingApi.getSubdistricts({ district_id: districtId, refresh });
      setLocations((current) => ({ ...current, subdistricts: data || [] }));
      setSelected((current) => ({ ...current, subdistrictId: "" }));
    } catch (error) {
      toast.error(error.message || "Gagal memuat kelurahan");
    } finally {
      setLocationLoading("");
    }
  };

  const calculateCost = async (refresh = false) => {
    if (!costForm.origin || !costForm.destination) {
      return toast.error("Kota asal dan kota tujuan wajib diisi");
    }
    if (String(costForm.origin) === String(costForm.destination)) {
      return toast.error("Kota asal dan kota tujuan tidak boleh sama.");
    }
    if (!costForm.weight || Number(costForm.weight) <= 0) {
      return toast.error("Berat paket wajib lebih dari 0 gram");
    }

    try {
      setCalculating(true);
      const data = await shippingApi.calculateCost({
        origin: Number(costForm.origin),
        destination: Number(costForm.destination),
        weight: Number(costForm.weight),
        price: costForm.price,
        calculation_type: "domestic",
        refresh,
      });
      setCostResult(data);
      toast.success(data.cached ? "Ongkir diambil dari cache database" : "Ongkir dihitung dari RajaOngkir dan disimpan");
    } catch (error) {
      toast.error(error.message || "Gagal menghitung ongkir");
    } finally {
      setCalculating(false);
    }
  };

  const chooseLocationAsDestination = () => {
    const destination = selected.cityId;
    if (!destination) return toast.error("Pilih kota/kabupaten dulu");
    setCostForm((current) => ({ ...current, destination }));
  };

  const originLocationOptions = useMemo(() => {
    return (originPicker.cities || []).map((item) => ({
      value: String(item.external_id),
      label: item.name,
    }));
  }, [originPicker.cities]);

  const destinationLocationOptions = useMemo(() => {
    return (destinationPicker.cities || []).map((item) => ({
      value: String(item.external_id),
      label: item.name,
    }));
  }, [destinationPicker.cities]);

  const filteredCostRows = useMemo(() => {
    const keyword = costSearch.trim().toLowerCase();
    const rows = costResult?.data || [];
    if (!keyword) return rows;
    return rows.filter((item) =>
      [item.name, item.code, item.service, item.description, item.etd]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(keyword)),
    );
  }, [costResult, costSearch]);

  const loadPickerProvinces = async (type) => {
    const data = await shippingApi.getProvinces({ refresh: false });
    if (type === "origin") {
      setOriginPicker((current) => ({ ...current, provinces: data || [] }));
    } else {
      setDestinationPicker((current) => ({ ...current, provinces: data || [] }));
    }
  };

  const loadPickerCities = async (type, provinceId) => {
    if (!provinceId) return;
    const data = await shippingApi.getCities({ province_id: provinceId, refresh: false });
    if (type === "origin") {
      setOriginPicker((current) => ({ ...current, cities: data || [], districts: [], subdistricts: [], cityId: "", districtId: "" }));
    } else {
      setDestinationPicker((current) => ({ ...current, cities: data || [], districts: [], subdistricts: [], cityId: "", districtId: "" }));
    }
  };

  const normalizeLocationName = (value) =>
    String(value || "")
      .trim()
      .toUpperCase()
      .replace(/\s+/g, " ");

  const findLocationByName = (items, name) =>
    (items || []).find((item) => normalizeLocationName(item.name) === normalizeLocationName(name));

  const applyMojorotoOriginPreset = async () => {
    try {
      const provinceList =
        originPicker.provinces?.length > 0
          ? originPicker.provinces
          : await shippingApi.getProvinces({ refresh: false });
      const province = findLocationByName(provinceList, MOJOROTO_PRESET.province);
      if (!province) return toast.error("Preset gagal: Provinsi Jawa Timur tidak ditemukan");

      setOriginPicker((current) => ({
        ...current,
        provinces: provinceList,
        provinceId: String(province.external_id),
        cityId: "",
        districtId: "",
        cities: [],
        districts: [],
        subdistricts: [],
      }));

      const cityList = await shippingApi.getCities({
        province_id: String(province.external_id),
        refresh: false,
      });
      const city = findLocationByName(cityList, MOJOROTO_PRESET.city);
      if (!city) return toast.error("Preset gagal: Kota Kediri tidak ditemukan");

      setOriginPicker((current) => ({
        ...current,
        cities: cityList || [],
        cityId: String(city.external_id),
        districtId: "",
        districts: [],
        subdistricts: [],
      }));

      setCostForm((current) => ({ ...current, origin: String(city.external_id) }));
      toast.success("Default origin Mojoroto diterapkan");
    } catch (error) {
      toast.error(error.message || "Gagal menerapkan preset Mojoroto");
    }
  };

  return (
    <AdminLayout>
      <main className="mx-auto max-w-7xl overflow-hidden px-4 py-8 sm:px-6 lg:px-8" data-testid="admin-couriers-page">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-emerald-700">
              Admin Pengiriman
            </p>
            <h1 className="mt-1 text-3xl font-semibold text-stone-950">
              Kurir & Tembolok RajaOngkir
            </h1>
            <p className="mt-2 max-w-3xl text-stone-600">
              Kelola kurir aktif, tembolok data wilayah RajaOngkir, dan simpan hasil hitung ongkir agar permintaan yang sama tidak selalu memanggil API berbayar.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={loadCouriers} disabled={loading}>
              <RefreshCw className="mr-2 h-4 w-4" /> Muat Ulang
            </Button>
            <Button variant="outline" onClick={seedDefault} disabled={saving}>
              <Database className="mr-2 h-4 w-4" /> Siapkan Kurir Default
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={openNew} className="bg-emerald-600 text-white hover:bg-emerald-700">
                  <Plus className="mr-2 h-4 w-4" /> Tambah Kurir
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{form.id ? "Ubah Kurir" : "Tambah Kurir"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={saveCourier} className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label>Kode</Label>
                      <Input value={form.code} onChange={(e) => setField("code", e.target.value)} placeholder="jne" />
                    </div>
                    <div>
                      <Label>Nama</Label>
                      <Input value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="JNE" />
                    </div>
                  </div>
                  <div>
                    <Label>Deskripsi</Label>
                    <Input value={form.description} onChange={(e) => setField("description", e.target.value)} placeholder="Jalur Nugraha Ekakurir" />
                  </div>
                  <div>
                    <Label>Urutan</Label>
                    <Input type="number" value={form.sort_order} onChange={(e) => setField("sort_order", e.target.value)} />
                  </div>
                  <div className="flex items-center justify-between rounded-lg border border-stone-200 bg-stone-50 p-4">
                    <div>
                      <p className="font-medium text-stone-900">Aktif</p>
                      <p className="text-sm text-stone-500">Kurir aktif akan dipakai untuk hitung ongkir.</p>
                    </div>
                    <Switch checked={form.is_active} onCheckedChange={(value) => setField("is_active", value)} />
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Batal
                    </Button>
                    <Button type="submit" disabled={saving} className="bg-emerald-600 text-white hover:bg-emerald-700">
                      Simpan
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Tabs defaultValue="couriers" className="space-y-5">
          <TabsList className="h-auto w-full justify-start gap-1 overflow-x-auto rounded-lg border border-stone-200 bg-white p-1 whitespace-nowrap">
            <TabsTrigger value="couriers">Kurir</TabsTrigger>
            <TabsTrigger value="locations">Tembolok Lokasi</TabsTrigger>
            <TabsTrigger value="cost">Hitung Ongkir</TabsTrigger>
          </TabsList>

          <TabsContent value="couriers">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="h-5 w-5 text-emerald-700" /> Daftar Kurir
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="hidden overflow-x-auto rounded-lg border border-stone-200 md:block">
                  <table className="w-full min-w-[700px] text-left text-sm">
                    <thead className="bg-stone-50 text-stone-500">
                      <tr>
                        <th className="px-4 py-3 font-medium">Kode</th>
                        <th className="px-4 py-3 font-medium">Nama</th>
                        <th className="px-4 py-3 font-medium">Status</th>
                        <th className="px-4 py-3 font-medium text-right">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100 bg-white">
                      {loading ? (
                        <tr>
                          <td className="px-4 py-10 text-center text-stone-500" colSpan={4}>
                            Memuat kurir...
                          </td>
                        </tr>
                      ) : couriers.length === 0 ? (
                        <tr>
                          <td className="px-4 py-10 text-center text-stone-500" colSpan={4}>
                            Belum ada kurir. Klik Siapkan Kurir Default untuk membuat daftar awal.
                          </td>
                        </tr>
                      ) : (
                        paginatedCouriers.map((courier) => (
                          <tr key={courier.id}>
                            <td className="px-4 py-3 font-mono text-stone-900">{courier.code}</td>
                            <td className="px-4 py-3">
                              <p className="font-medium text-stone-900">{courier.name}</p>
                              {courier.description && (
                                <p className="text-xs text-stone-500">{courier.description}</p>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${courier.is_active ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>
                                {courier.is_active ? "Aktif" : "Nonaktif"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="inline-flex gap-2">
                                <Button variant="outline" size="sm" onClick={() => openEdit(courier)}>
                                  <Pencil className="mr-1 h-4 w-4" /> Ubah
                                </Button>
                                <DeleteCourier courier={courier} onConfirm={() => removeCourier(courier)} />
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="space-y-3 md:hidden">
                  {loading ? (
                    <div className="rounded-xl border border-stone-200 bg-white px-4 py-8 text-center text-sm text-stone-500">
                      Memuat kurir...
                    </div>
                  ) : couriers.length === 0 ? (
                    <div className="rounded-xl border border-stone-200 bg-white px-4 py-8 text-center text-sm text-stone-500">
                      Belum ada kurir. Klik Siapkan Kurir Default untuk membuat daftar awal.
                    </div>
                  ) : (
                    paginatedCouriers.map((courier) => (
                      <div key={courier.id} className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-mono text-xs text-stone-500">{courier.code}</p>
                            <p className="font-semibold text-stone-900">{courier.name}</p>
                            {courier.description && <p className="mt-1 text-sm text-stone-500">{courier.description}</p>}
                          </div>
                          <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${courier.is_active ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>
                            {courier.is_active ? "Aktif" : "Nonaktif"}
                          </span>
                        </div>
                        <div className="mt-4 flex gap-2">
                          <Button variant="outline" size="sm" className="flex-1" onClick={() => openEdit(courier)}>
                            <Pencil className="mr-1 h-4 w-4" /> Ubah
                          </Button>
                          <DeleteCourier courier={courier} onConfirm={() => removeCourier(courier)} className="flex-1" />
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <AdminPagination
                  page={courierPage}
                  totalItems={couriers.length}
                  pageSize={PAGE_SIZE}
                  onPageChange={setCourierPage}
                  itemLabel="kurir"
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="locations">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="h-5 w-5 text-emerald-700" /> Tembolok Lokasi Bertingkat
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={() => loadProvinces(false)} disabled={locationLoading === "province"}>
                    Muat Provinsi
                  </Button>
                  <Button variant="outline" onClick={() => loadProvinces(true)} disabled={locationLoading === "province"}>
                    Segarkan dari RajaOngkir
                  </Button>
                </div>
                <div className="grid gap-4 lg:grid-cols-4">
                  <LocationSelect
                    label="Provinsi"
                    value={selected.provinceId}
                    items={locations.provinces}
                    onChange={(value) => {
                      setSelected((current) => ({ ...current, provinceId: value }));
                      loadCities(value, false);
                    }}
                  />
                  <LocationSelect
                    label="Kota/Kabupaten"
                    value={selected.cityId}
                    items={locations.cities}
                    disabled={!selected.provinceId}
                    onChange={(value) => {
                      setSelected((current) => ({ ...current, cityId: value }));
                      loadDistricts(value, false);
                    }}
                  />
                  <LocationSelect
                    label="Kecamatan"
                    value={selected.districtId}
                    items={locations.districts}
                    disabled={!selected.cityId}
                    onChange={(value) => {
                      setSelected((current) => ({ ...current, districtId: value }));
                      loadSubdistricts(value, false);
                    }}
                  />
                  <LocationSelect
                    label="Kelurahan"
                    value={selected.subdistrictId}
                    items={locations.subdistricts}
                    disabled={!selected.districtId}
                    onChange={(value) => setSelected((current) => ({ ...current, subdistrictId: value }))}
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" disabled={!selected.provinceId} onClick={() => loadCities(selected.provinceId, true)}>
                    Segarkan Kota
                  </Button>
                  <Button variant="outline" disabled={!selected.cityId} onClick={() => loadDistricts(selected.cityId, true)}>
                    Segarkan Kecamatan
                  </Button>
                  <Button variant="outline" disabled={!selected.districtId} onClick={() => loadSubdistricts(selected.districtId, true)}>
                    Segarkan Kelurahan
                  </Button>
                  <Button onClick={chooseLocationAsDestination} className="bg-emerald-600 text-white hover:bg-emerald-700">
                    Pakai sebagai Tujuan
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="cost">
            <div className="grid min-w-0 gap-5 lg:grid-cols-[420px_1fr]">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator className="h-5 w-5 text-emerald-700" /> Hitung Ongkir
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="rounded-lg border border-stone-200 bg-stone-50 p-3">
                    <p className="mb-3 text-sm font-medium text-stone-800">Pilih kota asal dan kota tujuan pengiriman</p>
                    <div className="grid gap-4 lg:grid-cols-2">
                      <div className="rounded-md border border-stone-200 bg-white p-3">
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-emerald-700">Asal</p>
                        <div className="grid gap-2">
                          <LocationSelect
                            label="Provinsi"
                            value={originPicker.provinceId}
                            items={originPicker.provinces}
                            onChange={async (value) => {
                              setOriginPicker((current) => ({ ...current, provinceId: value, cityId: "", districtId: "", cities: [], districts: [], subdistricts: [] }));
                              await loadPickerCities("origin", value);
                            }}
                          />
                          <LocationSelect
                            label="Kota/Kabupaten"
                            value={originPicker.cityId}
                            items={originPicker.cities}
                            disabled={!originPicker.provinceId}
                            onChange={async (value) => {
                              setOriginPicker((current) => ({ ...current, cityId: value, districtId: "", districts: [], subdistricts: [] }));
                              setCostForm((current) => ({ ...current, origin: value }));
                            }}
                          />
                        </div>
                      </div>
                      <div className="rounded-md border border-stone-200 bg-white p-3">
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-blue-700">Tujuan</p>
                        <div className="grid gap-2">
                          <LocationSelect
                            label="Provinsi"
                            value={destinationPicker.provinceId}
                            items={destinationPicker.provinces}
                            onChange={async (value) => {
                              setDestinationPicker((current) => ({ ...current, provinceId: value, cityId: "", districtId: "", cities: [], districts: [], subdistricts: [] }));
                              await loadPickerCities("destination", value);
                            }}
                          />
                          <LocationSelect
                            label="Kota/Kabupaten"
                            value={destinationPicker.cityId}
                            items={destinationPicker.cities}
                            disabled={!destinationPicker.provinceId}
                            onChange={async (value) => {
                              setDestinationPicker((current) => ({ ...current, cityId: value, districtId: "", districts: [], subdistricts: [] }));
                              setCostForm((current) => ({ ...current, destination: value }));
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                        onClick={applyMojorotoOriginPreset}
                      >
                        Default Origin Mojoroto
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label>Asal</Label>
                    <SearchableLocationSelect
                      value={costForm.origin}
                      items={originLocationOptions}
                      placeholder="Pilih kota asal..."
                      emptyText="Data kota kosong. Pilih provinsi dulu."
                      onChange={(value) => setCostForm((current) => ({ ...current, origin: value }))}
                    />
                  </div>
                  <div>
                    <Label>Tujuan</Label>
                    <SearchableLocationSelect
                      value={costForm.destination}
                      items={destinationLocationOptions}
                      placeholder="Pilih kota tujuan..."
                      emptyText="Data kota kosong. Pilih provinsi dulu."
                      onChange={(value) => setCostForm((current) => ({ ...current, destination: value }))}
                    />
                  </div>
                  <div>
                    <Label>Berat Paket (gram)</Label>
                    <Input type="number" value={costForm.weight} onChange={(e) => setCostForm((current) => ({ ...current, weight: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Strategi Harga</Label>
                    <Select value={costForm.price} onValueChange={(value) => setCostForm((current) => ({ ...current, price: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="lowest">Termurah</SelectItem>
                        <SelectItem value="highest">Termahal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="rounded-lg border border-stone-200 bg-stone-50 p-3 text-sm text-stone-600">
                    Kurir aktif: {activeCouriers.length ? activeCouriers.map((item) => item.code).join(", ") : "belum ada"}
                  </div>
                  <Button onClick={() => calculateCost(false)} disabled={calculating} className="w-full bg-emerald-600 text-white hover:bg-emerald-700">
                    Hitung
                  </Button>
                </CardContent>
              </Card>
              <Card className="min-w-0 overflow-hidden">
                <CardHeader>
                  <CardTitle>Hasil Ongkir</CardTitle>
                </CardHeader>
                <CardContent>
                  {!costResult ? (
                    <div className="rounded-lg border border-dashed border-stone-300 p-10 text-center text-stone-500">
                      Hasil hitung ongkir akan muncul di sini.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className={`rounded-full px-2.5 py-1 font-medium ${costResult.cached ? "bg-sky-100 text-sky-700" : "bg-emerald-100 text-emerald-700"}`}>
                          {costResult.cached ? "Dari cache database" : "Baru dari RajaOngkir"}
                        </span>
                        <span className="text-stone-500">Kurir: {costResult.courier}</span>
                        <span className="text-stone-500">Direct domestic-cost</span>
                      </div>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
                        <Input
                          className="pl-9"
                          value={costSearch}
                          onChange={(event) => setCostSearch(event.target.value)}
                          placeholder="Cari kurir, layanan, deskripsi, atau estimasi..."
                        />
                      </div>
                      <div className="overflow-x-auto rounded-lg border border-stone-200">
                        <table className="w-full min-w-[680px] text-left text-sm">
                          <thead className="bg-stone-50 text-stone-500">
                            <tr>
                              <th className="px-4 py-3 font-medium">Kurir</th>
                              <th className="px-4 py-3 font-medium">Layanan</th>
                              <th className="px-4 py-3 font-medium">Estimasi</th>
                              <th className="px-4 py-3 font-medium text-right">Ongkir</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-stone-100 bg-white">
                            {filteredCostRows.map((item, index) => (
                              <tr key={`${item.code}-${item.service}-${index}`}>
                                <td className="px-4 py-3">
                                  <p className="font-medium text-stone-900">{item.name}</p>
                                  <p className="font-mono text-xs text-stone-500">{item.code}</p>
                                </td>
                                <td className="px-4 py-3">
                                  <p className="font-medium text-stone-900">{item.service}</p>
                                  <p className="text-xs text-stone-500">{item.description}</p>
                                </td>
                                <td className="px-4 py-3 text-stone-700">{item.etd || "-"}</td>
                                <td className="px-4 py-3 text-right font-semibold text-stone-900">
                                  {formatRupiah(Number(item.cost || 0))}
                                </td>
                              </tr>
                            ))}
                            {filteredCostRows.length === 0 && (
                              <tr>
                                <td colSpan={4} className="px-4 py-8 text-center text-stone-500">
                                  Tidak ada layanan yang cocok dengan pencarian.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </AdminLayout>
  );
};

const LocationSelect = ({ label, value, items, onChange, disabled = false }) => (
  <div>
    <Label>{label}</Label>
    <Select value={value} onValueChange={onChange} disabled={disabled || items.length === 0}>
      <SelectTrigger>
        <SelectValue placeholder={items.length ? `Pilih ${label}` : "Belum ada data"} />
      </SelectTrigger>
      <SelectContent>
        {items.map((item) => (
          <SelectItem key={item.external_id} value={String(item.external_id)}>
            {item.name}{item.zip_code ? ` (${item.zip_code})` : ""}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const DeleteCourier = ({ courier, onConfirm, className = "" }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <Button variant="outline" size="sm" className={`border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800 ${className}`}>
        <Trash2 className="mr-1 h-4 w-4" /> Hapus
      </Button>
    </AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Hapus {courier.name}?</AlertDialogTitle>
        <AlertDialogDescription>
          Kurir ini akan hilang dari database admin. Untuk sementara biasanya lebih aman menonaktifkan kurir lewat tombol Ubah.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Batal</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="bg-rose-600 hover:bg-rose-700">
          Ya, Hapus
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const SearchableLocationSelect = ({ value, items, onChange, placeholder, emptyText }) => {
  const [open, setOpen] = useState(false);
  const selected = items.find((item) => item.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          <span className="truncate text-left">{selected ? selected.label : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Cari lokasi..." />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {items.map((item) => (
                <CommandItem
                  key={item.value}
                  value={`${item.label} ${item.value}`}
                  onSelect={() => {
                    onChange(item.value);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === item.value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="truncate">{item.label}</span>
                  <span className="ml-auto text-xs text-stone-400">#{item.value}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default AdminCouriersPage;
